import styled from 'styled-components'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Typography } from 'components/Typography/Typography'
import { Stylable } from 'utils/types'
import { frozenVaults, VaultDisplayInfo, vaultDisplayInfoList } from 'utils/vaultDisplayInfo'
import { VaultsList } from './VaultsList'
import { Direction, SortBy, SortConfig, VaultListRow, VaultNumberUpdate } from '../types'
import { DONT_SHOW_THIS_AGAIN_LS_KEY } from 'modules/VaultDetails/VaultDetails'
import { WhatIsSingleVault } from '../WhatIsSignleVault'
import { SingleVaultExplanation } from 'modules/VaultDetails/SingleVaultExplanation'
import { getSortedVaults } from './utils'
import { SearchInput } from 'components/SearchInput/SearchInput'

interface Props extends Stylable {
  isHomePage?: boolean
  searchQuery?: string
}

const VaultsWrapper = styled.div`
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 0px 20px;
  }
`

const VaultsContainer = styled.div`
  max-width: 1200px;
  margin: auto;
  position: relative;
`

const VaultsSearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
    margin-top: 80px;
    margin-bottom: 0;

    & > div {
      font-size: ${p => p.theme.typography.size.Subtitle2}px;
      margin-bottom: 16px;
    }
  }
`

const getInitialStateData = (vaultDisplayInfoList: VaultDisplayInfo[]): VaultListRow[] => {
  return vaultDisplayInfoList.map(v => ({
    ...v,
    [SortBy.Apy]: undefined,
    [SortBy.TvlUsd]: undefined,
    [SortBy.DepositedUsd]: undefined,
    [SortBy.EarnedUsd]: undefined,
  }))
}

const isInitialStateActive = (vaultListRow: VaultListRow[]) => {
  // Everything from table numbers needs to be undefined in case of initialState
  for (const vault of vaultListRow) {
    if (
      vault[SortBy.Apy] !== undefined ||
      vault[SortBy.EarnedUsd] !== undefined ||
      vault[SortBy.DepositedUsd] !== undefined ||
      vault[SortBy.TvlUsd] !== undefined
    ) {
      return false
    }
  }

  return true
}

export const VaultsListContainer = ({ style, isHomePage, searchQuery }: Props) => {
  const [isDontShowSelected, setIsDontShowSelected] = useState(
    localStorage.getItem(DONT_SHOW_THIS_AGAIN_LS_KEY) === 'true'
  )

  const setDontShowThisAgain = useCallback(() => {
    setIsDontShowSelected(true)
    localStorage.setItem(DONT_SHOW_THIS_AGAIN_LS_KEY, 'true')
  }, [])

  const resetDontShowThisAgain = useCallback(() => {
    setIsDontShowSelected(false)
    localStorage.setItem(DONT_SHOW_THIS_AGAIN_LS_KEY, 'false')
  }, [])

  const [searchText, setSearchText] = useState(searchQuery || '')
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: null,
  })

  const [sortConfigFrozen, setSortConfigFrozen] = useState<SortConfig>({
    key: null,
    direction: null,
  })

  useEffect(() => {
    setSearchText(searchQuery || '')
  }, [searchQuery])

  const [displayRows, setDisplayRows] = useState<VaultListRow[]>(getInitialStateData(vaultDisplayInfoList))

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }, [])

  const handleClear = useCallback(() => {
    setSearchText('')
  }, [])

  const handleColumnHeaderClick = useCallback(
    (key: SortBy) => {
      let direction = Direction.Descending
      if (sortConfig.key !== SortBy.Vault && sortConfig.key === key && sortConfig.direction === Direction.Descending) {
        direction = Direction.Ascending
      }
      setSortConfig({ key, direction })
    },
    [sortConfig]
  )

  const handleColumnHeaderClickFrozen = useCallback(
    (key: SortBy) => {
      let direction = Direction.Descending
      if (
        sortConfigFrozen.key !== SortBy.Vault &&
        sortConfigFrozen.key === key &&
        sortConfigFrozen.direction === Direction.Descending
      ) {
        direction = Direction.Ascending
      }
      setSortConfigFrozen({ key, direction })
    },
    [sortConfigFrozen]
  )

  const updateVaultInfoListNumbers = useCallback((updateObj: VaultNumberUpdate) => {
    const { vaultId, apy, tvlUsd, earnedUsd, depositedUsd } = updateObj
    setDisplayRows(prevDisplayRows => {
      const newDisplayRows = prevDisplayRows.map(d => {
        if (d.vault.id === vaultId) {
          return {
            ...d,
            [SortBy.Apy]: apy,
            [SortBy.TvlUsd]: tvlUsd,
            [SortBy.EarnedUsd]: earnedUsd,
            [SortBy.DepositedUsd]: depositedUsd,
          }
        } else {
          return {
            ...d,
          }
        }
      })
      return newDisplayRows.sort((a, b) => (b.tvlUsd ?? 0) - (a.tvlUsd ?? 0))
    })
  }, [])

  const displayData = useMemo(() => {
    const displayVaults = displayRows.filter(v => v.tSymbol.toLowerCase().indexOf(searchText.toLowerCase()) !== -1)

    if (!sortConfig.key) {
      return displayVaults
    }

    const sortableItems = [...displayVaults]

    return getSortedVaults(displayVaults, sortableItems, sortConfig)
  }, [displayRows, sortConfig, searchText])

  return (
    <VaultsWrapper style={style}>
      <VaultsContainer>
        {!isHomePage && (
          <>
            <WhatIsSingleVault
              isDontShowSelected={isDontShowSelected}
              onResetDontShowThisAgain={resetDontShowThisAgain}
            />
            <SingleVaultExplanation
              style={{ margin: '32px 0' }}
              isDontShowSelected={isDontShowSelected}
              onDontShowThisAgainClick={setDontShowThisAgain}
            />
          </>
        )}
        {!isHomePage && (
          <VaultsSearchContainer>
            <Typography variant="title2">
              All <span style={{ fontWeight: 700 }}>Vaults</span>
            </Typography>
            <SearchInput text={searchText} onChange={handleInputChange} onClear={handleClear} />
          </VaultsSearchContainer>
        )}
        <VaultsList
          displayData={displayData}
          displayFrozenData={frozenVaults}
          sortConfig={sortConfig}
          sortConfigFrozen={sortConfigFrozen}
          onColumnHeaderClick={handleColumnHeaderClick}
          onColumnFrozenHeaderClick={handleColumnHeaderClickFrozen}
          updateVaultInfoListNumbers={updateVaultInfoListNumbers}
          isInitial={isInitialStateActive(displayRows)}
        />
      </VaultsContainer>
    </VaultsWrapper>
  )
}
